// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
// import Axios from 'axios';

import NotificationItem, { NotificationItemDTO } from '@shared/models/dashboard/notification-item';
import BaseService from '@core/services/base';
import { ListRequestParams, ListRequestResponse, PageInfo } from '@shared/types/services';
import { REQUEST_PARAMS } from '@shared/constants/services';
import { NOTIFICATION_ITEM_FIELDS } from '@shared/constants/dashboard';

// Notes:
// - BaseService references service::create(), edit(), delete() so may need to implement them here.
@injectable()
export default class NotificationItemsService extends BaseService<NotificationItemDTO, NotificationItem> {
  static diToken = Symbol('notification-items-service');

  constructor() {
    // domainArea is the URL prefix (endpoint) which goes under Dashboard.
    super({ domainArea: 'dashboard/notification', Model: NotificationItem });
  }

  // Get a notification with a given Id from the portal-api.
  async get(id: NotificationItemDTO['id']): Promise<NotificationItem> {
    try {
      const url = `/dashboard/notification/${id}`;
      // const { data } = await Axios.get(url);
      const data: NotificationItemDTO = {
        id,
        customerId: 1,
        customer: 'customerStr',
        assetId: 1,
        asset: 'assetStr',
        jobRequestId: 1,
        jobRequest: 'jqString',
        date: '2021-01-01',
        event: url,
      };

      return new NotificationItem(data);
    } catch (err) {
      throw err;
    }
  }

  // Setup a query structure to retrieve data.
  getListQuery({ pagination, sorting, filtering }: ListRequestParams) {
    // WXYZ - 'items' does not look right.  May need to be the same as NotificationItemDTO.
    return `
      {
        notificationItems(
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize}, 
          sort: { field: ${sorting?.field}, type: ${sorting?.type}},
          ${REQUEST_PARAMS.filtering.fields.searchKeyword}: "${filtering?.searchKeyword || ''}",
        ) {
          items {
            ${NOTIFICATION_ITEM_FIELDS.id},
            ${NOTIFICATION_ITEM_FIELDS.customer},              
            ${NOTIFICATION_ITEM_FIELDS.asset},            
            ${NOTIFICATION_ITEM_FIELDS.jobRequest},            
            ${NOTIFICATION_ITEM_FIELDS.date},            
            ${NOTIFICATION_ITEM_FIELDS.event}
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  // Get a list of notifications from the portal-api with items selected based on a query structure (item and page info).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getList(params: ListRequestParams) {

    const dto1: NotificationItemDTO = {
      id: 1,
      customerId: 24,
      customer: 'cust 24',
      assetId: 2,
      asset: 'asset 2',
      jobRequestId: 1,
      jobRequest: 'jr 1',
      date: '2021-01-02',
      event: 'event 1 - Data collected for XYZ.'
    };
    
    const dto2: NotificationItemDTO = {
      id: 2,
      customerId: 1,
      customer: 'cust 1',
      assetId: 6,
      asset: 'asset 6',
      jobRequestId: 1,
      jobRequest: 'jr 1',
      date: '2021-02-02',
      event: 'event 2 - Mission XYZ canceled due to bad weather.'
    };
    
    const dto3: NotificationItemDTO = {
      id: 3,
      customerId: 12,
      customer: 'cust 12',
      assetId: 1,
      asset: 'asset 1',
      jobRequestId: 1,
      jobRequest: 'jr 1',
      date: '2021-03-02',
      event: 'event 3 - First test flight for XYZ camera system.'
    };

    const pageInfTmp: PageInfo = {
      totalCount: 3,
      hasPreviousPage: false,
      hasNextPage: false,
    };

    const data = {
      notificationItems: {
        items: [dto1, dto2, dto3],
        pageInfo: pageInfTmp
      }
    };
   
    const {
      notificationItems: { items, pageInfo },
    } = data;

    return {
      pageInfo,
      items: items.map((dto) => new NotificationItem(dto)),
    };
  }

  // cut-paste, probably not needed.
  getInfoFromListResponse(data: { [key: string]: ListRequestResponse<NotificationItem> }) {
    const {
      notificationItems: { items, pageInfo },
    } = data;

    return { items, pageInfo };
  }
}
